/**
 * @fileoverview Display helpers for the department-wide occupancy stepper.
 * @module system-web/services/departmentWideStepper
 *
 * Responsibilities:
 * - Group adapter steps into Foundations → Documents tiers.
 * - Flag completed steps that still have row-level failures (amber).
 * - Convert recognized migration internals into actionable administrator messages.
 *
 * Dependencies: none (pure). Must stay in sync with api/packages/legacy-migration departmentWide.ts.
 *
 * Architecture: Web copy of the frozen DAG display rules — system-web cannot import the API package.
 *
 * Notes: Raw diagnostic errors remain persisted server-side; this module changes presentation only.
 *
 * @localFirst
 * Source of Truth: Dexie run.steps (already loaded by the hook)
 * Write Flow: none
 * Sync Strategy: N/A
 * Conflict Resolution: N/A
 */

import { REHOST_COPY_STALE_MS, isRehostCopyStale } from '@firesquirrel/sync-config';

export { REHOST_COPY_STALE_MS, isRehostCopyStale };

export const DEPARTMENT_WIDE_TIERS: ReadonlyArray<{ id: string; label: string; adapterKeys: readonly string[] }> = [
  { id: 'foundations', label: 'Foundations', adapterKeys: ['referenceLookups', 'buildingTypeLookups', 'constructionTypeLookups', 'udfBlueprints'] },
  { id: 'buildings', label: 'Buildings', adapterKeys: ['inspectableEntities'] },
  { id: 'hydrants', label: 'Hydrants', adapterKeys: ['hydrantMain'] },
  { id: 'people', label: 'People & notes', adapterKeys: ['occupancyContacts', 'buildingInformationalItems', 'communicationLogs'] },
  { id: 'systems', label: 'Systems', adapterKeys: ['buildingSystems'] },
  { id: 'hazmat', label: 'Hazmat & tanks', adapterKeys: ['masterChemicals', 'occupancyChemicals', 'occupancyTanks'] },
  { id: 'admin', label: 'Admin', adapterKeys: ['occupancyPermits', 'riskProfiles', 'tceCompliance'] },
  { id: 'crr', label: 'Community Risk', adapterKeys: ['crrProfile', 'crrDetector', 'crrDetectorEvent'] },
  { id: 'documents', label: 'Documents', adapterKeys: ['fileMetadata'] },
];

/**
 * @reusable
 * Usage: MigrationProgressSidebar grouping.
 * Rules: presentation-only; leftover keys go under Other.
 */
export function groupDepartmentWideStepsByTier<T extends { adapterKey: string }>(
  steps: readonly T[],
): Array<{ id: string; label: string; steps: T[] }> {
  const byKey = new Map(steps.map((s) => [s.adapterKey, s]));
  const used = new Set<string>();
  const groups: Array<{ id: string; label: string; steps: T[] }> = [];
  for (const tier of DEPARTMENT_WIDE_TIERS) {
    const bucket = tier.adapterKeys
      .map((key) => byKey.get(key))
      .filter((s): s is T => Boolean(s));
    if (bucket.length === 0) continue;
    for (const s of bucket) used.add(s.adapterKey);
    groups.push({ id: tier.id, label: tier.label, steps: bucket });
  }
  const leftover = steps.filter((s) => !used.has(s.adapterKey));
  if (leftover.length > 0) {
    groups.push({ id: 'other', label: 'Other', steps: leftover as T[] });
  }
  return groups;
}

/**
 * @reusable
 * Usage: Amber badge when a completed step still has row failures.
 */
export function isCompletedWithRowFailures(step: { status: string; counts?: { failed?: number } }): boolean {
  return step.status === 'completed' && (step.counts?.failed ?? 0) > 0;
}

export interface RehostCounts {
  copied: number;
  pending: number;
  failed: number;
  skipped?: number;
  total: number;
}

/**
 * @contract OverlayableChildRun
 * Purpose: Latest single-dataset re-run used to paint live counts on a frozen
 *          department-wide step card without rewriting that historical report.
 * Fields:  status, counts, processed, total, phase, message, heartbeatAt, overallPercent, _id
 * Rules:   Latest child attempt drives live, completed, failed, or cancelled effective status without mutating history.
 * Sync:    Dexie mirror of legacyMigrationRuns; parent steps stay server-authoritative history.
 */
export interface OverlayableChildRun {
  _id: string;
  status: 'pending' | 'running' | 'continued' | 'completed' | 'failed' | 'cancelled';
  counts?: { selected?: number; created?: number; updated?: number; unchanged?: number; skipped?: number; failed?: number };
  processed?: number;
  total?: number;
  phase?: 'extracting' | 'writing';
  message?: string;
  heartbeatAt?: string;
  overallPercent?: number;
  startedAt?: string;
  cancelRequested?: boolean;
  selectedLegacyIds?: string[];
  /** Receipt-backed source IDs for this child. Empty means no receipts were found, not that none migrated. */
  migratedLegacyIds?: string[];
  failedRows?: Array<{ legacyId: string; error: string }>;
  failedReasonSummary?: Array<{ error: string; count: number }>;
  skippedRows?: Array<{ legacyId: string; error: string }>;
  skippedReasonSummary?: Array<{ error: string; count: number }>;
  rehostStatus?: 'not_started' | 'running' | 'completed' | 'partial_failed';
  rehostCancelRequested?: boolean;
  rehostCounts?: RehostCounts;
  rehostErrorSummary?: Array<{ errorType: string; count: number; sampleMessage?: string | null; sampleStage?: string | null }>;
  rehostStartedAt?: string;
  updatedAt?: string;
}

function indexRowsByError(rows: ReadonlyArray<{ legacyId: string; error: string }> | undefined): Map<string, string> {
  const byId = new Map<string, string>();
  for (const row of rows ?? []) {
    if (row.legacyId && !byId.has(row.legacyId)) byId.set(row.legacyId, row.error);
  }
  return byId;
}

function idSet(ids: readonly string[] | undefined): Set<string> {
  return new Set((ids ?? []).map((id) => String(id).trim()).filter(Boolean));
}

const UNINDEXED_EXCEPTION_REASON = 'unindexed_exception';

function parentExceptionTotal(summary: ReadonlyArray<{ count: number }> | undefined): number {
  return (summary ?? []).reduce((sum, item) => sum + Math.max(0, item.count), 0);
}

/**
 * Reason counts merge by summation, then every record proven to have left the
 * parent bucket (`drainedIds`) decrements the reason it was previously filed
 * under. Rows outside the saved ID sample stay in `unindexed_exception`:
 * their previous reason is unknown, so they are not assigned to another group.
 */
function mergeDrainedReasonSummaries(
  parent: ReadonlyArray<{ error: string; count: number }> | undefined,
  child: ReadonlyArray<{ error: string; count: number }> | undefined,
  parentById: ReadonlyMap<string, string>,
  drainedIds: ReadonlySet<string>,
  unindexedDrainCount: number = 0,
): Array<{ error: string; count: number }> {
  const merged = new Map<string, number>();
  for (const item of [...(parent ?? []), ...(child ?? [])]) {
    if (item.error === UNINDEXED_EXCEPTION_REASON) continue;
    merged.set(item.error, (merged.get(item.error) ?? 0) + item.count);
  }
  for (const id of drainedIds) {
    const previousError = parentById.get(id);
    if (previousError !== undefined) merged.set(previousError, (merged.get(previousError) ?? 0) - 1);
  }
  const indexed = [...merged.values()].reduce((sum, count) => sum + Math.max(0, count), 0);
  const unindexed = Math.max(0, parentExceptionTotal(parent) - drainedIds.size - unindexedDrainCount + parentExceptionTotal(child) - indexed);
  if (unindexed > 0) merged.set(UNINDEXED_EXCEPTION_REASON, unindexed);
  return [...merged]
    .filter(([, count]) => count > 0)
    .map(([error, count]) => ({ error, count }));
}

/**
 * Effective row list after a partial rerun: drained parent rows drop out, child
 * rows (re-)enter with the latest error. Capped like the server-side lists.
 */
function mergeExceptionRowLists(
  parentRows: ReadonlyArray<{ legacyId: string; error: string }> | undefined,
  childRows: ReadonlyArray<{ legacyId: string; error: string }> | undefined,
  drainedIds: ReadonlySet<string>,
): Array<{ legacyId: string; error: string }> {
  const merged = new Map<string, { legacyId: string; error: string }>();
  for (const row of parentRows ?? []) {
    if (!drainedIds.has(row.legacyId)) merged.set(row.legacyId, row);
  }
  for (const row of childRows ?? []) merged.set(row.legacyId, row);
  return [...merged.values()].slice(0, 2000);
}

/**
 * @reusable
 * Usage: Department-wide report tiles while a Hydrants (or other) re-run is in flight.
 * Rules: Does not mutate the parent run. Live/newer child counts replace the frozen step line.
 */
function withUnchanged(count: number | undefined): { unchanged?: number } {
  return count ? { unchanged: count } : {};
}

/**
 * @reusable
 * Usage: Paint the latest single-dataset re-run on top of a frozen department-wide step.
 * Rules: Presentation-only. A full-dataset child replaces the step's counts and reason summaries.
 *        A partial child drains a parent exception only when `migratedLegacyIds` proves the child
 *        wrote a receipt for that exact ID. Processed-row counts are not identity. A missing
 *        receipt list (undefined) leaves every parent exception unresolved; an empty list is a
 *        completed lookup with no proven clears.
 */
export function overlayStepWithChildRun<T extends {
  adapterKey: string;
  status: string;
  percent: number;
  counts: { selected: number; created: number; updated: number; unchanged?: number; skipped: number; failed: number };
  processed?: number;
  total?: number;
  phase?: 'extracting' | 'writing';
  message?: string;
  heartbeatAt?: string;
  failedRows?: OverlayableChildRun['failedRows'];
  failedReasonSummary?: OverlayableChildRun['failedReasonSummary'];
  skippedRows?: OverlayableChildRun['skippedRows'];
  skippedReasonSummary?: OverlayableChildRun['skippedReasonSummary'];
}>(step: T, child: OverlayableChildRun | null | undefined): T & {
  overlayRunId?: string;
  overlayStartedAt?: string;
  failedRows?: OverlayableChildRun['failedRows'];
  failedReasonSummary?: OverlayableChildRun['failedReasonSummary'];
  skippedRows?: OverlayableChildRun['skippedRows'];
  skippedReasonSummary?: OverlayableChildRun['skippedReasonSummary'];
} {
  if (!child) return step;
  const cancelled = child.status === 'cancelled' || Boolean(child.cancelRequested);
  const live = !cancelled && isDepartmentWideRunLive(child.status);
  const childCounts = child.counts;
  if (child.status === 'failed') {
    return {
      ...step,
      status: 'failed',
      message: child.message,
      heartbeatAt: child.heartbeatAt,
      overlayRunId: child._id,
      overlayStartedAt: child.startedAt,
      failedRows: child.failedRows,
      failedReasonSummary: child.failedReasonSummary,
    };
  }
  if (child.status === 'completed' && childCounts) {
    const replacesWholeDataset = (childCounts.selected ?? child.total ?? 0) >= step.counts.selected;
    const retriedCount = childCounts.selected ?? child.total ?? 0;
    // Parent exception lists are capped at 2,000 IDs and a requested selection
    // is not proof the child processed the row. Drain only IDs a child receipt
    // proves were migrated, plus re-skips present in both complete child lists.
    // Counts of created/updated rows never stand in for a missing receipt.
    const parentSkippedById = indexRowsByError(step.skippedRows);
    const parentFailedById = indexRowsByError(step.failedRows);
    const migratedIds = idSet(child.migratedLegacyIds);
    const childSkippedIds = idSet((child.skippedRows ?? []).map((row) => row.legacyId));
    const duplicateSkipIds = new Set([...childSkippedIds].filter((id) => parentSkippedById.has(id)));
    const migratedExceptionIds = [...migratedIds].filter((id) => parentSkippedById.has(id) || parentFailedById.has(id));
    const skippedDrainIds = replacesWholeDataset
      ? new Set<string>()
      : new Set([...duplicateSkipIds, ...migratedExceptionIds.filter((id) => parentSkippedById.has(id))]);
    const failedDrainIds = replacesWholeDataset
      ? null
      : new Set(migratedExceptionIds.filter((id) => parentFailedById.has(id)));
    const provenFailureClears = failedDrainIds ? failedDrainIds.size : 0;
    const counts = replacesWholeDataset
      ? {
          selected: step.counts.selected,
          created: childCounts.created ?? 0,
          updated: childCounts.updated ?? 0,
          ...withUnchanged(childCounts.unchanged),
          skipped: childCounts.skipped ?? 0,
          failed: childCounts.failed ?? 0,
        }
      : {
          selected: step.counts.selected,
          created: step.counts.created + (childCounts.created ?? 0),
          updated: step.counts.updated + (childCounts.updated ?? 0),
          ...withUnchanged((step.counts.unchanged ?? 0) + (childCounts.unchanged ?? 0)),
          skipped: Math.max(0, step.counts.skipped - skippedDrainIds.size) + (childCounts.skipped ?? 0),
          failed: Math.max(0, step.counts.failed - provenFailureClears) + (childCounts.failed ?? 0),
        };
    const failedRows = retriedCount === 0
      ? step.failedRows
      : !replacesWholeDataset && failedDrainIds
        ? mergeExceptionRowLists(step.failedRows, child.failedRows, failedDrainIds)
        : child.failedRows?.length
          ? child.failedRows
          : (childCounts.failed ?? 0) === 0 ? [] : step.failedRows;
    const failedReasonSummary = retriedCount === 0
      ? step.failedReasonSummary
      : !replacesWholeDataset && failedDrainIds
        ? mergeDrainedReasonSummaries(step.failedReasonSummary, child.failedReasonSummary, parentFailedById, failedDrainIds)
        : child.failedReasonSummary?.length
          ? child.failedReasonSummary
          : (childCounts.failed ?? 0) === 0 ? [] : step.failedReasonSummary;
    const skippedRows = replacesWholeDataset
      ? child.skippedRows ?? []
      : mergeExceptionRowLists(step.skippedRows, child.skippedRows, skippedDrainIds);
    const skippedReasonSummary = replacesWholeDataset
      ? child.skippedReasonSummary ?? []
      : mergeDrainedReasonSummaries(step.skippedReasonSummary, child.skippedReasonSummary, parentSkippedById, skippedDrainIds);
    return {
      ...step,
      status: 'completed',
      percent: 100,
      counts,
      processed: child.processed ?? childCounts.selected,
      total: child.total ?? childCounts.selected,
      phase: child.phase,
      message: child.message,
      heartbeatAt: child.heartbeatAt,
      overlayRunId: child._id,
      overlayStartedAt: child.startedAt,
      failedRows,
      failedReasonSummary,
      skippedRows,
      skippedReasonSummary,
    };
  }
  if (!live && !cancelled) return step;
  return {
    ...step,
    status: cancelled ? 'cancelled' : child.status,
    percent: typeof child.overallPercent === 'number'
      ? child.overallPercent
      : live ? Math.max(step.percent, 5) : step.percent,
    counts: {
      selected: childCounts?.selected ?? step.counts.selected,
      created: childCounts?.created ?? 0,
      updated: childCounts?.updated ?? 0,
      ...withUnchanged(childCounts?.unchanged),
      skipped: childCounts?.skipped ?? 0,
      failed: childCounts?.failed ?? 0,
    },
    processed: child.processed,
    total: child.total,
    phase: child.phase,
    message: child.message,
    heartbeatAt: child.heartbeatAt,
    overlayRunId: child._id,
    overlayStartedAt: child.startedAt,
    failedRows: child.failedRows,
    failedReasonSummary: child.failedReasonSummary,
    skippedReasonSummary: child.skippedReasonSummary ?? [],
  };
}

/**
 * @reusable
 * Usage: Run dashboard when a dataset has multiple rerun attempts.
 * Rules: Applies attempts oldest-to-newest so targeted retries merge into the latest full-dataset result.
 */
export function overlayStepWithChildRuns<T extends Parameters<typeof overlayStepWithChildRun>[0]>(
  step: T,
  children: readonly OverlayableChildRun[],
): ReturnType<typeof overlayStepWithChildRun<T>> {
  return [...children]
    .sort((a, b) => String(a.startedAt ?? '').localeCompare(String(b.startedAt ?? '')))
    .reduce((effective, child) => overlayStepWithChildRun(effective, child), step) as ReturnType<typeof overlayStepWithChildRun<T>>;
}

/**
 * @reusable
 * Usage: Department dashboard / header copy line after a Documents tab rerun.
 * Rules: Latest child attempt with rehost fields replaces frozen parent rehostStatus.
 *        Historical parent run document is not mutated.
 */
/**
 * @reusable
 * Usage: Stop file copy must target the run that owns the running ledger, not an overlaid parent.
 * Rules: A Documents child with rehostStatus running wins. Otherwise the parent itself. Never an unrelated tab run.
 */
export function resolveRunningFileCopyRunId(
  candidates: ReadonlyArray<{ _id: string; datasetKey?: string; parentRunId?: string; rehostStatus?: string }>,
  parentRunId?: string,
): string | null {
  const running = candidates.filter((row) => row.rehostStatus === 'running');
  if (parentRunId) {
    const child = running.find((row) => row.parentRunId === parentRunId && row.datasetKey === 'fileMetadata');
    if (child) return child._id;
    const parent = running.find((row) => row._id === parentRunId);
    if (parent) return parent._id;
  }
  const standaloneDocuments = running.find((row) => row.datasetKey === 'fileMetadata' && !row.parentRunId);
  return standaloneDocuments?._id ?? null;
}

export function overlayRehostFromChildRuns(
  parent: {
    rehostStatus?: 'not_started' | 'running' | 'completed' | 'partial_failed';
    rehostCancelRequested?: boolean;
    message?: string;
    rehostCounts?: RehostCounts;
    rehostErrorSummary?: OverlayableChildRun['rehostErrorSummary'];
    heartbeatAt?: string;
    rehostStartedAt?: string;
    updatedAt?: string;
  },
  children: ReadonlyArray<OverlayableChildRun | null | undefined>,
): {
  rehostStatus?: 'not_started' | 'running' | 'completed' | 'partial_failed';
  rehostCancelRequested?: boolean;
  message?: string;
  rehostCounts?: RehostCounts;
  rehostErrorSummary?: OverlayableChildRun['rehostErrorSummary'];
  heartbeatAt?: string;
  rehostStartedAt?: string;
  updatedAt?: string;
} {
  const sorted = [...children]
    .filter((child): child is OverlayableChildRun => Boolean(child && (child.rehostStatus || child.rehostCounts)))
    .sort((a, b) => String(a.startedAt ?? '').localeCompare(String(b.startedAt ?? '')));
  const latest = sorted[sorted.length - 1];
  if (!latest) {
    return {
      rehostStatus: parent.rehostStatus,
      rehostCancelRequested: parent.rehostCancelRequested,
      message: parent.message,
      rehostCounts: parent.rehostCounts,
      rehostErrorSummary: parent.rehostErrorSummary,
      heartbeatAt: parent.heartbeatAt,
      rehostStartedAt: parent.rehostStartedAt,
      updatedAt: parent.updatedAt,
    };
  }
  return {
    rehostStatus: latest.rehostStatus ?? parent.rehostStatus,
    rehostCancelRequested: latest.rehostCancelRequested,
    message: latest.message,
    rehostCounts: latest.rehostCounts ?? parent.rehostCounts,
    rehostErrorSummary: latest.rehostErrorSummary ?? [],
    heartbeatAt: latest.heartbeatAt ?? parent.heartbeatAt,
    rehostStartedAt: latest.rehostStartedAt ?? parent.rehostStartedAt,
    updatedAt: latest.updatedAt ?? parent.updatedAt,
  };
}

/**
 * @contract EffectiveDepartmentMigrationSummary
 * Purpose: Run-centric status derived from the latest effective result for every department dataset.
 * Fields: status, counts, completedDatasets, remainingDatasetKeys, failedRows.
 * Rules: Latest rerun overlays are supplied by the caller; historical parent steps remain unchanged.
 * Sync: Pure derivation from Dexie-backed parent and child run records.
 */
export interface EffectiveDepartmentMigrationSummary {
  status: 'running' | 'completed' | 'needs-attention';
  counts: { selected: number; created: number; updated: number; skipped: number; failed: number };
  completedDatasets: number;
  remainingDatasetKeys: string[];
  failedRows: number;
}

/**
 * @reusable
 * Usage: Department migration dashboard and compact header card.
 * Rules: Sums effective step counts and treats any non-completed or failed-row step as remaining work.
 */
/**
 * A resumed field-response slice used to save the SQL total plus the rows already written.
 * That made 4.8 million display as 6.7 million and the gap as never processed.
 * When the saved total is more than double the rows this step actually counted, keep the counted rows.
 */
export function correctedStepSelected(step: {
  adapterKey?: string;
  processed?: number;
  counts: { selected: number; created: number; updated: number; skipped: number; failed: number; unchanged?: number };
}): number {
  const counted = (step.processed ?? 0) || (step.counts.created + step.counts.updated + (step.counts.unchanged ?? 0) + step.counts.skipped + step.counts.failed);
  if (step.adapterKey === 'inspectionFieldResponses' && counted > 0 && step.counts.selected > counted * 2 && step.counts.selected - counted > 1_000_000) {
    return counted;
  }
  return step.counts.selected;
}

/**
 * @reusable
 * Usage: Department dashboard "datasets clear" and remaining-work list.
 * Rules: A completed step with no failed rows is clear. A skipped step is clear
 * only when the adapter ran and recorded an empty source. Zero counts also mean
 * the executor never queried the dataset, so those skipped steps stay unfinished.
 * Failed, pending, and running steps stay in remaining work.
 */
export function isDepartmentDatasetClear(step: {
  status: string;
  counts: { selected?: number; created?: number; updated?: number; unchanged?: number; skipped?: number; failed?: number };
  message?: string;
  finishedAt?: Date | string;
}): boolean {
  if ((step.counts.failed ?? 0) > 0) return false;
  if (step.status === 'completed') return true;
  if (step.status !== 'skipped') return false;
  const selected = step.counts.selected ?? 0;
  const written = (step.counts.created ?? 0) + (step.counts.updated ?? 0) + (step.counts.unchanged ?? 0) + (step.counts.skipped ?? 0);
  if (selected !== 0 || written !== 0) return false;
  return Boolean(step.finishedAt) && /no records in legacy system/i.test(step.message ?? '');
}

export function summarizeEffectiveDepartmentMigration(
  steps: ReadonlyArray<{
    adapterKey: string;
    status: string;
    counts: { selected: number; created: number; updated: number; skipped: number; failed: number };
  }>,
): EffectiveDepartmentMigrationSummary {
  const counts = steps.reduce((sum, step) => {
    const isPatchDataset = step.adapterKey === 'inspectionFieldResponses';
    return {
      selected: sum.selected + (isPatchDataset ? 0 : correctedStepSelected(step)),
      created: sum.created + (isPatchDataset ? 0 : step.counts.created),
      updated: sum.updated + (isPatchDataset ? 0 : step.counts.updated),
      skipped: sum.skipped + (isPatchDataset ? 0 : step.counts.skipped),
      failed: sum.failed + step.counts.failed,
    };
  }, { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 });
  const remainingDatasetKeys = steps
    .filter((step) => !isDepartmentDatasetClear(step))
    .map((step) => step.adapterKey);
  const running = steps.some((step) => isDepartmentWideRunLive(step.status));
  return {
    status: running ? 'running' : remainingDatasetKeys.length > 0 ? 'needs-attention' : 'completed',
    counts,
    completedDatasets: steps.length - remainingDatasetKeys.length,
    remainingDatasetKeys,
    failedRows: counts.failed,
  };
}

const DOCUMENT_TRANSFER_LEDGER_CONFLICT = /Updating the path ['"]attempts['"] would create a conflict at ['"]attempts['"]/i;
const DOCUMENT_ATTACHMENT_ARRAY_MISSING = /The path ['"]attachments['"] must exist in the document in order to apply array updates/i;
const DOCUMENT_TRANSFER_LEDGER_MESSAGE = 'Document metadata was saved, but the file could not be queued for transfer. Retry the Documents migration. If it fails again, contact support with the run ID.';
const DOCUMENT_ATTACHMENT_ARRAY_MESSAGE = 'Document metadata could not be added to its related record. Retry the Documents migration. If it fails again, contact support with the run ID.';

/**
 * @reusable
 * Usage: Run history and report views before rendering a persisted migration failure.
 * Rules: Presentation-only; translates only recognized technical signatures and preserves all unknown errors unchanged.
 */
const PARENT_BUILDING_MISSING = /Parent building entity '([0-9a-f]{24})' not found/i;

export function formatMigrationFailureForAdmin(error: string | undefined): string {
  const normalized = (error ?? 'Unknown error').replace(/\s+/g, ' ').trim() || 'Unknown error';
  if (DOCUMENT_TRANSFER_LEDGER_CONFLICT.test(normalized)) return DOCUMENT_TRANSFER_LEDGER_MESSAGE;
  if (DOCUMENT_ATTACHMENT_ARRAY_MISSING.test(normalized)) return DOCUMENT_ATTACHMENT_ARRAY_MESSAGE;
  if (PARENT_BUILDING_MISSING.test(normalized)) return 'Parent occupancy was not migrated.';
  return normalized;
}

/**
 * @contract MigrationIssueRecord
 * Purpose: One skipped or failed source row, described with the Streamline id a user can look up.
 * Fields: sourceId, inspectionSeriesId, fieldId, reason, verify.
 * Rules: Never requires the user to read a Mongo ObjectId. The internal parent id is omitted.
 * Sync: Presentation only.
 */
export interface MigrationIssueRecord {
  sourceId: string;
  inspectionSeriesId?: string;
  fieldId?: string;
  reason: string;
  verify: string;
}

/**
 * @reusable
 * Usage: Report popup — turn a stored skip/failure into the source record the user must verify.
 * Rules: Presentation-only. Mongo ids in the error text are not shown as the thing to look up.
 */
export function describeMigrationIssue(legacyId: string, error: string | undefined): MigrationIssueRecord {
  const normalized = (error ?? '').replace(/\s+/g, ' ').trim();
  const seriesField = /^(\d+):f_(\d+)$/.exec(legacyId);
  if (seriesField) {
    return {
      sourceId: seriesField[1],
      inspectionSeriesId: seriesField[1],
      fieldId: `f_${seriesField[2]}`,
      reason: formatMigrationSkipReasonForAdmin(normalized || 'missing_parent_case'),
      verify: `Check inspection series ${seriesField[1]} in Streamline for this department. Field ${seriesField[2]}. Confirm it is not deleted, has an occupancy or hydrant, and has a status the cases step can map.`,
    };
  }
  if (PARENT_BUILDING_MISSING.test(normalized)) {
    return {
      sourceId: legacyId,
      reason: 'Parent occupancy was not migrated.',
      verify: `Open occ_ins_history ID ${legacyId} in Streamline and confirm its occupancy (f_frusa_occup_id) exists and was included in Occupancies.`,
    };
  }
  return {
    sourceId: legacyId,
    reason: formatMigrationSkipReasonForAdmin(normalized || 'Unknown error'),
    verify: `Look up Streamline ID ${legacyId}.`,
  };
}

/**
 * @reusable
 * Usage: Collapse one-line-per-Mongo-id failure summaries into one cause.
 * Rules: Presentation-only. Counts are summed. The original id list stays available on the rows.
 */
export function collapseMigrationIssueSummaries(
  reasons: ReadonlyArray<{ error: string; count: number }>,
): Array<{ error: string; count: number }> {
  const counts = new Map<string, number>();
  for (const reason of reasons) {
    const label = formatMigrationFailureForAdmin(reason.error);
    counts.set(label, (counts.get(label) ?? 0) + reason.count);
  }
  return [...counts.entries()]
    .map(([error, count]) => ({ error, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * @reusable
 * Usage: Occupancies Run History — group byRow / failedRows into count + administrator-readable reason.
 * Rules: Presentation-only; known errors are translated before grouping and unknown errors retain diagnostic text.
 */
export function summarizeFailedRows(
  rows: ReadonlyArray<{ action?: string; error?: string }>,
): Array<{ error: string; count: number }> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (row.action && row.action !== 'failed') continue;
    const error = formatMigrationFailureForAdmin(row.error);
    counts.set(error, (counts.get(error) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([error, count]) => ({ error, count }))
    .sort((a, b) => b.count - a.count);
}

export const STEP_HEARTBEAT_STALE_MS = 120_000;

/**
 * @reusable
 * Usage: Sidebar line — "12 / 500 updating" or extract wait text.
 * Rules: presentation-only.
 */
export function formatStepProgressLine(step: {
  status?: string;
  phase?: string;
  processed?: number;
  total?: number;
  message?: string;
  displayName?: string;
}): string {
  const total = step.total ?? 0;
  if (total > 0 && step.phase !== 'extracting') {
    const message = step.message?.trim();
    if (step.phase === 'writing' && message && !message.startsWith('Reading ')) return message;
    return `${step.processed ?? 0} / ${total} updating`;
  }
  if (step.message?.trim()) return step.message.trim();
  if (step.phase === 'extracting') {
    return `Reading ${step.displayName ?? 'records'} from Streamline SQL…`;
  }
  return '';
}

/**
 * @reusable
 * Usage: Department report tile. A queued dataset must not look like an active SQL read.
 * Rules: Presentation-only. "Reading from Streamline" is reserved for a step whose phase is extracting.
 */
/**
 * @reusable
 * Usage: Subtitle counts on a department report tile.
 * Rules: If legacy SQL has 0 records, say "No records in legacy system" instead of confusing "0 created · 0 updated".
 *        For inspection cases and field responses, render status-first verifiable text instead of raw counts.
 */
export function formatDepartmentStepTileCounts(
  counts: {
    selected?: number;
    created?: number;
    updated?: number;
    unchanged?: number;
    skipped?: number;
    failed?: number;
  },
  step?: {
    adapterKey?: string;
    status?: string;
  },
): string {
  const selected = counts.selected ?? 0;
  const created = counts.created ?? 0;
  const updated = counts.updated ?? 0;
  const unchanged = counts.unchanged ?? 0;
  const skipped = counts.skipped ?? 0;
  const failed = counts.failed ?? 0;

  if (selected === 0 && created === 0 && updated === 0 && unchanged === 0 && skipped === 0 && failed === 0) {
    return 'No records in legacy system';
  }

  if (step?.adapterKey === 'inspectionFieldResponses') {
    const issues: string[] = [];
    if (skipped > 0) issues.push(`${skipped} skipped`);
    if (failed > 0) issues.push(`${failed} failed`);
    return issues.length > 0
      ? `Answers linked to cases · ${issues.join(' · ')}`
      : 'Answers linked to cases';
  }

  if (step?.adapterKey === 'inspectionCasesAndEvents') {
    const migrated = created + updated + unchanged;
    const issues: string[] = [];
    if (skipped > 0) issues.push(`${skipped} skipped`);
    if (failed > 0) issues.push(`${failed} failed`);
    const label = migrated > 0 ? `${migrated} cases linked` : 'Cases linked';
    return issues.length > 0 ? `${label} · ${issues.join(' · ')}` : label;
  }

  const parts: string[] = [];
  if (created > 0) parts.push(`${created} created`);
  if (updated > 0) parts.push(`${updated} updated`);
  if (unchanged > 0) parts.push(`${unchanged} unchanged`);
  if (created === 0 && updated === 0 && unchanged === 0) parts.push('0 migrated');
  if (skipped > 0) parts.push(`${skipped} skipped`);
  if (failed > 0) parts.push(`${failed} failed`);

  return parts.join(' · ');
}

export function formatDepartmentStepTileStatus(step: {
  status?: string;
  phase?: string;
  processed?: number;
  total?: number;
  message?: string;
  displayName?: string;
  startedAt?: Date | string;
}, options?: { runStale?: boolean; retrying?: boolean }): string {
  if (options?.retrying) return 'Starting re-run…';
  if (isDepartmentStepWaiting(step)) {
    return options?.runStale
      ? 'Not started — department migration is paused'
      : 'Waiting — not started yet';
  }
  const line = formatStepProgressLine(step);
  if (line) return line;
  if (step.phase === 'writing') return 'Writing to tenant database';
  if (step.phase === 'extracting') return 'Reading from Streamline';
  return 'Waiting — not started yet';
}

/**
 * @reusable
 * Usage: Report tile spinner. A queued dataset is not an active read.
 * Rules: Presentation-only. Pending with no extract or write progress is waiting, not live.
 */
export function isDepartmentStepWaiting(step: {
  status?: string;
  phase?: string;
  processed?: number;
  total?: number;
  message?: string;
  startedAt?: Date | string;
}): boolean {
  return step.status === 'pending'
    && !step.phase
    && !step.startedAt
    && !(step.processed && step.processed > 0)
    && !(step.total && step.total > 0)
    && !step.message?.trim();
}

/**
 * @reusable
 * Usage: Warn when a running step has not heartbeated for 2 minutes.
 */
export function isStepHeartbeatStale(
  heartbeatAt: Date | string | undefined,
  nowMs: number = Date.now(),
  staleMs: number = STEP_HEARTBEAT_STALE_MS,
  startedAt?: Date | string,
): boolean {
  const reference = heartbeatAt ?? startedAt;
  if (!reference) return false;
  const t = reference instanceof Date ? reference.getTime() : new Date(reference).getTime();
  if (!Number.isFinite(t)) return false;
  return nowMs - t > staleMs;
}

/**
 * @reusable
 * Usage: Any UI that must not treat a worker handoff as a finished department migration.
 * Rules: `continued` is the checkpoint written before the next replica starts. It is still live.
 */
export function isDepartmentWideRunLive(status: string | undefined): boolean {
  return status === 'running' || status === 'pending' || status === 'continued';
}

/**
 * @reusable
 * Usage: Keep a finished department report refreshing while files are still being copied.
 * Rules: Poll only when the copy is running or files are still pending. A finished copy with pending 0 does not poll.
 */
/**
 * @reusable
 * Usage: Report heading for the field-response window chosen on this run.
 * Rules: 0 is all responses. A missing value is not recorded. Do not invent a date.
 */
export function formatFieldResponseWindow(months: number | undefined): string {
  if (months === undefined || !Number.isInteger(months) || months < 0) return 'Date window not recorded';
  if (months === 0) return 'All inspection field responses, no date cutoff';
  if (months % 12 === 0) {
    const years = months / 12;
    return `Past ${years} ${years === 1 ? 'year' : 'years'}`;
  }
  return `Past ${months} ${months === 1 ? 'month' : 'months'}`;
}

/**
 * @reusable
 * Usage: View Report sign-off — how many Streamline inspections the chosen window wrote.
 * Rules: Counts come from the completed Inspection Field Responses step. updated is the inspection-series count.
 *        A running step is not reported as complete.
 */
export function formatWindowedInspectionResult(
  windowMonths: number | undefined,
  step: {
    status?: string;
    counts?: { selected?: number; updated?: number; skipped?: number; failed?: number };
  } | undefined,
): string | null {
  if (!step || step.status !== 'completed') return null;
  const inspections = step.counts?.updated ?? 0;
  const answers = step.counts?.selected ?? 0;
  const skipped = step.counts?.skipped ?? 0;
  const failed = step.counts?.failed ?? 0;
  const windowLabel = formatFieldResponseWindow(windowMonths);
  const issues = [
    skipped > 0 ? `${skipped.toLocaleString()} skipped` : '',
    failed > 0 ? `${failed.toLocaleString()} failed` : '',
  ].filter(Boolean).join(', ');
  return `${inspections.toLocaleString()} Streamline inspections migrated for ${windowLabel} (${answers.toLocaleString()} field responses)${issues ? ` · ${issues}` : ''}.`;
}

export function shouldPollFileCopyCounts(run: {
  status?: string;
  rehostStatus?: string;
  rehostCounts?: { pending?: number; total?: number };
} | null | undefined): boolean {
  if (!run || isDepartmentWideRunLive(run.status)) return false;
  if (run.rehostStatus === 'running') return true;
  const pending = run.rehostCounts?.pending ?? 0;
  const total = run.rehostCounts?.total ?? 0;
  return pending > 0 && total > 0;
}

/**
 * @reusable
 * Usage: Legacy Migration page. A stale department sidebar must not hide a separately started dataset run.
 * Rules: Presentation-only. The department sidebar owns Resume for its own run; a live tab run keeps its own progress and Stop control.
 */
export function shouldShowDatasetProgressStrip(input: {
  departmentWideLive: boolean;
  hasStripRun: boolean;
  hasLiveTabRun: boolean;
}): boolean {
  return input.hasStripRun && (!input.departmentWideLive || input.hasLiveTabRun);
}

/**
 * @reusable
 * Usage: The left department list and the top status line, so a dead handoff is not labelled Migrated or In progress.
 * Rules: A run with no heartbeat for 2 minutes is paused even when its stored status is still running or continued.
 */
export const STEP_PROGRESS_STALE_MS = 5 * 60 * 1000;

export type DepartmentRunActivity = 'writing' | 'restarting' | 'stalled' | 'paused' | 'stopping' | 'idle';

/**
 * @reusable
 * Usage: Header and left-list badge so a frozen count is not labelled In progress.
 * Rules: Presentation-only. A fresh heartbeat with no new record for 5 minutes is stalled.
 *        No heartbeat for 2 minutes is paused. A restart message before the next record is restarting.
 */
export function departmentRunActivity(run: {
  status?: string;
  cancelRequested?: boolean;
  heartbeatAt?: Date | string;
  updatedAt?: Date | string;
  startedAt?: Date | string;
  steps?: Array<{
    status?: string;
    message?: string;
    heartbeatAt?: Date | string;
    progressedAt?: Date | string;
    startedAt?: Date | string;
    processed?: number;
  }>;
} | null | undefined, nowMs: number = Date.now()): DepartmentRunActivity {
  if (!run || !isDepartmentWideRunLive(run.status)) return 'idle';
  if (run.cancelRequested) return 'stopping';
  if (isDepartmentWideRunStale(run, nowMs)) return 'paused';
  const step = run.steps?.find((item) => item.status === 'running');
  if (step && isStepHeartbeatStale(step.heartbeatAt, nowMs, STEP_HEARTBEAT_STALE_MS, step.startedAt)) return 'paused';
  if (step?.message?.startsWith('Worker restarted')) return 'restarting';
  const progressReference = step?.progressedAt ?? step?.startedAt;
  const progressAge = progressReference ? nowMs - new Date(progressReference).getTime() : 0;
  if (step && (step.processed ?? 0) > 0 && Number.isFinite(progressAge) && progressAge > STEP_PROGRESS_STALE_MS) return 'stalled';
  return 'writing';
}

export function isDepartmentWideRunStale(run: {
  status?: string;
  heartbeatAt?: Date | string;
  updatedAt?: Date | string;
  startedAt?: Date | string;
} | null | undefined, nowMs: number = Date.now()): boolean {
  if (!run || !isDepartmentWideRunLive(run.status)) return false;
  return isStepHeartbeatStale(run.heartbeatAt ?? run.updatedAt, nowMs, STEP_HEARTBEAT_STALE_MS, run.startedAt);
}

/**
 * @reusable
 * Usage: Single-dataset report page activity guard when a worker has not emitted its first heartbeat.
 * Rules: Presentation-only; live runs use heartbeatAt, then updatedAt, then startedAt as the activity reference.
 */
export function isMigrationRunHeartbeatStale(
  run: { status: string; heartbeatAt?: Date | string; updatedAt?: Date | string; startedAt?: Date | string },
  nowMs: number = Date.now(),
  staleMs: number = STEP_HEARTBEAT_STALE_MS,
): boolean {
  if (!isDepartmentWideRunLive(run.status)) return false;
  const reference = run.heartbeatAt ?? run.updatedAt ?? run.startedAt;
  if (!reference) return true;
  const timestamp = reference instanceof Date ? reference.getTime() : new Date(reference).getTime();
  if (!Number.isFinite(timestamp)) return true;
  return nowMs - timestamp > staleMs;
}

export const DEPARTMENT_WIDE_ADAPTER_KEYS: readonly string[] = DEPARTMENT_WIDE_TIERS.flatMap((t) => t.adapterKeys);

/**
 * @reusable
 * Usage: Dataset tab strip. Hydrants sit before Contacts because Contacts depends on hydrant entities.
 * Rules: Frozen department-wide DAG is unchanged; this is display order only.
 */
export const DATASET_TAB_ORDER: readonly string[] = (() => {
  const keys = [...DEPARTMENT_WIDE_ADAPTER_KEYS];
  const hydrantIdx = keys.indexOf('hydrantMain');
  const contactsIdx = keys.indexOf('occupancyContacts');
  if (hydrantIdx > contactsIdx && contactsIdx >= 0) {
    keys.splice(hydrantIdx, 1);
    keys.splice(contactsIdx, 0, 'hydrantMain');
  }
  return keys;
})();

const DOCUMENTS_DATASET_KEY = 'fileMetadata';

/**
 * @reusable
 * Usage: Sort GET /datasets + static stubs into operator tab order.
 * Rules: Known occupancy keys follow DATASET_TAB_ORDER (Hydrants before Contacts). Unregistered-in-tiers
 *        datasets keep relative order after them. Documents (fileMetadata) is always the last tab.
 */
export function sortAdaptersForMigrationTabs<T extends { key: string }>(adapters: readonly T[]): T[] {
  const rank = new Map(DATASET_TAB_ORDER.filter((key) => key !== DOCUMENTS_DATASET_KEY).map((key, index) => [key, index]));
  const rankOf = (key: string): number => {
    if (key === DOCUMENTS_DATASET_KEY) return Number.MAX_SAFE_INTEGER;
    return rank.get(key) ?? rank.size;
  };
  return [...adapters].sort((a, b) => rankOf(a.key) - rankOf(b.key));
}

/**
 * @reusable
 * Usage: Documents copy line on the latest-department chip and Documents tab.
 * Rules: presentation-only; never includes SAS, URLs, or filenames.
 */
const REHOST_ERROR_MESSAGES: Record<string, string> = {
  sas_expired: 'Legacy storage SAS expired or returned 403. Rotate LEGACY_STORAGE_SAS_URL and retry Documents.',
  sas_not_configured: 'Legacy storage SAS is not configured on the copy worker. Set LEGACY_STORAGE_SAS_URL and retry.',
  unresolvable_source_url: 'Source file path could not be joined to the legacy storage account. Check att_file_name / hiResURL.',
  unreadable_source: 'Source blob could not be read (empty or inaccessible). Confirm the file exists in Streamline storage.',
  http_404: 'Source blob was not found (404). Confirm the file still exists in Streamline storage.',
  sql_empty: 'SQL binary column was empty. The occupancy photo may only exist as a blob filename.',
  missing_parent: 'Parent occupancy/system record is missing. Migrate Occupancies (and related parents) first.',
  metadata_link_failed: 'File was copied to storage, but could not be linked to its tank, chemical, or system record. Retry failed files to link it; the file is not uploaded again.',
  network_error: 'Network error while reading the legacy storage or writing to attachment storage. Retry failed files.',
  storage_not_configured: 'Attachment storage is not configured on the copy worker. Check the storage settings, then retry failed files.',
  upload_failed: 'File could not be uploaded to attachment storage after retries. Retry failed files; share the detail line if it repeats.',
};

const MIGRATION_SKIP_MESSAGES: Record<string, string> = {
  missing_parent: 'Parent record is not in FireSquirrel yet. Migrate its parent (Inspections or Occupancies) first, then re-run Documents.',
  missing_parent_case: 'Parent inspection case was not migrated. Run Inspection Cases and Events first, then re-run this dataset.',
  missing_parent_case_deleted: 'Parent inspection case was migrated before, but its record is missing from FireSquirrel now. Re-run Inspection Cases and Events to rewrite it, then re-run Inspection Field Responses.',
  missing_parent_case_upstream: 'Parent inspection case was skipped in Inspection Cases and Events. Fix that step first, then re-run Inspection Field Responses.',
  source_deleted: 'Deleted in Streamline, so there is nothing to copy.',
  unknown_table: 'Document source type is not supported by the migration.',
  skipped_without_reason: 'Skipped by migration rules (no reason was recorded).',
  unindexed_exception: 'Not migrated. The saved report sample does not name this record, so it was not cleared by a later retry.',
};

const MISSING_PARENT_CASE_UPSTREAM_PREFIX = 'missing_parent_case_upstream:';

/**
 * @reusable
 * Usage: Run History — explain why a record or document was not migrated.
 * Rules: Known codes are translated; `missing_parent_case_upstream:` carries the
 *        upstream Cases-run reason inline; other text is passed through the failure formatter unchanged.
 */
export function formatMigrationSkipReasonForAdmin(error: string | undefined): string {
  const code = (error ?? '').trim();
  if (code.startsWith(MISSING_PARENT_CASE_UPSTREAM_PREFIX)) {
    const reason = code.slice(MISSING_PARENT_CASE_UPSTREAM_PREFIX.length).trim();
    return `Parent inspection case was skipped in Inspection Cases and Events — ${reason}. Fix that step first, then re-run Inspection Field Responses.`;
  }
  return MIGRATION_SKIP_MESSAGES[code] ?? formatMigrationFailureForAdmin(error);
}

/**
 * @reusable
 * Usage: Run History expanded row — translate ledger errorType without SAS/URLs/filenames.
 * Rules: Unknown codes keep a generic copy-failed message plus the raw code.
 */
export function formatRehostErrorType(errorType: string | undefined): string {
  const code = (errorType ?? 'upload_failed').trim() || 'upload_failed';
  const legacyHttp = /^http_(\d{3})$/.exec(code);
  const storageHttp = /^storage_http_(\d{3})$/.exec(code);
  return REHOST_ERROR_MESSAGES[code]
    ?? (legacyHttp ? `Legacy storage returned HTTP ${legacyHttp[1]}. Retry failed files.` : undefined)
    ?? (storageHttp ? `Attachment storage returned HTTP ${storageHttp[1]}. Check the copy worker storage access, then retry failed files.` : undefined)
    ?? `File copy failed (${code}).`;
}

export interface RunOutcomeLine {
  text: string;
  count: number;
  detail?: string;
}

export interface RunOutcome {
  tone: 'success' | 'warning' | 'failure' | 'pending';
  headline: string;
  recordsLine: string | null;
  notMigrated: RunOutcomeLine[];
  filesLine: string | null;
  fileIssues: RunOutcomeLine[];
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * @reusable
 * Usage: Plain-language final description of a migration run for administrators.
 * Rules: Presentation-only. Never includes SAS, URLs, or filenames (sample messages are already sanitized server-side).
 */
export function describeRunOutcome(run: {
  datasetKey?: string;
  isGlobal?: boolean;
  mode?: string;
  status: string;
  message?: string;
  rehostCancelRequested?: boolean;
  counts: { selected: number; created: number; updated: number; unchanged?: number; skipped: number; failed: number };
  rehostStatus?: 'not_started' | 'running' | 'completed' | 'partial_failed';
  rehostCounts?: RehostCounts;
  rehostErrorSummary?: Array<{ errorType: string; count: number; sampleMessage?: string | null; sampleStage?: string | null }>;
  heartbeatAt?: Date | string;
  rehostStartedAt?: Date | string;
  updatedAt?: Date | string;
  skippedReasonSummary?: Array<{ error: string; count: number }>;
  steps?: Array<{
    adapterKey?: string;
    displayName: string;
    status?: string;
    counts?: { selected?: number; created?: number; updated?: number; skipped?: number; failed?: number };
    skippedReasonSummary?: Array<{ error: string; count: number }>;
  }>;
}): RunOutcome {
  const rc = run.rehostCounts;
  const unchangedCount = run.counts.unchanged ?? 0;
  const isDepartmentWide = Boolean(run.isGlobal || run.datasetKey === 'department-wide' || ((run.steps ?? []).length > 1));
  const nonPatchSteps = isDepartmentWide
    ? (run.steps ?? []).filter((s) => s.adapterKey !== 'inspectionFieldResponses')
    : (run.steps ?? []);
  const effectiveSelected = (run.steps ?? []).length > 0
    ? nonPatchSteps.reduce((sum, step) => sum + correctedStepSelected({
        adapterKey: step.adapterKey,
        processed: (step as any).processed,
        counts: {
          selected: step.counts?.selected ?? 0,
          created: step.counts?.created ?? 0,
          updated: step.counts?.updated ?? 0,
          skipped: step.counts?.skipped ?? 0,
          failed: step.counts?.failed ?? 0,
        },
      }), 0)
    : run.counts.selected;
  const migratedCount = (run.steps ?? []).length > 0
    ? nonPatchSteps.reduce((sum, s) => sum + (s.counts?.created ?? 0) + (s.counts?.updated ?? 0) + ((s.counts as any)?.unchanged ?? 0), 0)
    : run.counts.created + run.counts.updated + unchangedCount;
  const skippedCount = (run.steps ?? []).length > 0
    ? nonPatchSteps.reduce((sum, s) => sum + (s.counts?.skipped ?? 0), 0)
    : run.counts.skipped;
  const failedCount = (run.steps ?? []).length > 0
    ? nonPatchSteps.reduce((sum, s) => sum + (s.counts?.failed ?? 0), 0)
    : run.counts.failed;
  // A field-response skip is one case-field group. That group can hold several source rows.
  const answerSteps = (run.steps ?? []).filter((step) => step.adapterKey === 'inspectionFieldResponses');
  const skippedAnswerGroupCount = answerSteps.reduce((sum, step) => sum + (step.counts?.skipped ?? 0), 0);
  // Answer failures are not entity outcomes. Counting them in `accounted`
  // hides the same number of unprocessed entities from the shortfall, but a
  // completed run can still carry them because the answer stream returns
  // completed with a positive failure count.
  const failedAnswerGroupCount = answerSteps.reduce((sum, step) => sum + (step.counts?.failed ?? 0), 0);
  const accounted = migratedCount + skippedCount + failedCount;
  const unprocessed = Math.max(0, effectiveSelected - accounted);
  const recordsLine = effectiveSelected > 0
    ? `Records: ${effectiveSelected} selected · ${migratedCount} migrated${unchangedCount ? ` (${unchangedCount} unchanged, not rewritten)` : ''} · ${skippedCount} not migrated · ${failedCount} failed${unprocessed ? ` · ${unprocessed} not processed` : ''}`
    : null;
  const skipSources = run.skippedReasonSummary?.length
    ? [{ label: '', items: run.skippedReasonSummary }]
    : (run.steps ?? []).filter((step) => step.skippedReasonSummary?.length)
      .map((step) => ({ label: `${step.displayName}: `, items: step.skippedReasonSummary ?? [] }));
  const notMigrated = skipSources.flatMap((source) => source.items.map((item) => ({
    text: `${source.label}${formatMigrationSkipReasonForAdmin(item.error)}`,
    count: item.count,
  })));
  const explainedDatasets = new Set(skipSources.map((source) => source.label.replace(/:\s*$/, '')));
  for (const step of run.steps ?? []) {
    const skipped = step.counts?.skipped ?? 0;
    if (skipped > 0 && !explainedDatasets.has(step.displayName) && !step.skippedReasonSummary?.length) {
      notMigrated.push({ text: `${step.displayName}: skipped by migration rules`, count: skipped });
    }
  }
  const filesLine = rc && rc.total > 0
    ? `Files: ${rc.copied} of ${rc.total} copied${rc.failed ? ` · ${rc.failed} failed` : ''}${rc.pending ? ` · ${rc.pending} waiting` : ''}`
    : null;
  const fileIssues = (run.rehostErrorSummary ?? []).map((group) => ({
    text: formatRehostErrorType(group.errorType),
    count: group.count,
    detail: [group.sampleStage, group.sampleMessage].filter(Boolean).join(' · ') || undefined,
  }));
  const base = { recordsLine, notMigrated, filesLine, fileIssues };

  if (run.mode === 'dry-run') return { ...base, tone: 'pending', headline: 'Dry run only. Nothing was written.' };
  if (run.status === 'pending' || run.status === 'running' || run.status === 'continued') {
    // 'continued' is a live handoff to another worker — report in-progress,
    // never a completed outcome while the badge still shows In Progress.
    return { ...base, tone: 'pending', headline: 'Migration in progress.' };
  }
  if (run.status === 'failed') {
    const stoppedShort = unprocessed > 0
      ? `Migration stopped after ${accounted} of ${effectiveSelected} records. ${unprocessed} were never processed.`
      : 'Migration failed. Review the reason below, fix it, then re-run.';
    return { ...base, tone: 'failure', headline: stoppedShort };
  }
  if (run.rehostStatus === 'running' && !isRehostCopyStale(run, Date.now())) {
    const leftover = skippedCount > 0 ? ` ${plural(skippedCount, 'record')} were not migrated.` : '';
    return { ...base, tone: skippedCount > 0 || failedCount > 0 ? 'warning' : 'pending', headline: `Copying files.${leftover}` };
  }
  if (run.rehostStatus === 'running' && isRehostCopyStale(run, Date.now())) {
    return {
      ...base,
      tone: 'warning',
      headline: 'File copy stopped before it finished. Copy the remaining files to continue. Already copied files are not uploaded again.',
    };
  }

  const copyBadge = resolveFileCopyBadge(run);
  const docStep = run.steps?.find((step) => step.adapterKey === DOCUMENTS_DATASET_KEY || step.displayName === 'Documents');
  const transferableFiles = rc
    ? rc.total
    : run.datasetKey === DOCUMENTS_DATASET_KEY
      ? run.counts.created + run.counts.updated
      : (docStep?.counts?.created ?? 0) + (docStep?.counts?.updated ?? 0);
  const filesNotStarted = copyBadge === 'not_copied' && transferableFiles > 0;
  const copyFailedWithoutCounts = copyBadge === 'copy_failed' && !(rc?.failed);
  const copyStopped = Boolean(run.rehostCancelRequested);
  const parts: string[] = [];
  if (unprocessed) parts.push(`${plural(unprocessed, 'record')} never processed`);
  if (skippedCount) parts.push(`${plural(skippedCount, 'record')} skipped`);
  if (skippedAnswerGroupCount) parts.push(`${plural(skippedAnswerGroupCount, 'answer group')} not linked`);
  if (failedAnswerGroupCount) parts.push(`${plural(failedAnswerGroupCount, 'answer group')} failed`);
  if (failedCount) parts.push(`${plural(failedCount, 'record')} failed`);
  if (rc?.failed) parts.push(`${plural(rc.failed, 'file')} failed to copy`);
  else if (copyFailedWithoutCounts) parts.push('file copy failed');
  if (rc?.pending) {
    if (copyStopped) parts.push(`file copy stopped (${plural(rc.pending, 'file')} pending)`);
    else if (run.rehostStatus === 'running') parts.push(`${plural(rc.pending, 'file')} copying`);
    else parts.push(`${plural(rc.pending, 'file')} pending copy`);
  } else if (filesNotStarted) parts.push('files not copied');
  if (parts.length === 0) {
    const headline = copyBadge === 'copied' || (rc?.copied ?? 0) > 0
      ? 'All records and files migrated successfully.'
      : 'All records migrated successfully.';
    return { ...base, tone: 'success', headline };
  }
  const tone = failedCount > 0 || failedAnswerGroupCount > 0 || (rc?.failed ?? 0) > 0 || copyFailedWithoutCounts ? 'failure' : 'warning';
  return { ...base, tone, headline: `Completed with issues: ${parts.join(', ')}.` };
}

export type RunOutcomeInput = Parameters<typeof describeRunOutcome>[0];

type EffectiveRunStep = NonNullable<RunOutcomeInput['steps']>[number] & {
  adapterKey: string;
  status: string;
  counts: { selected: number; created: number; updated: number; skipped: number; failed: number; unchanged?: number };
};

/**
 * @reusable
 * Usage: Department sign-off — describe the effective latest-attempt view of a run instead of the frozen parent.
 * Rules: Pure; the parent document is never mutated. Counts, skip reasons, and rehost data come from effective steps
 *        and child overlays. Any effective failed step forces a failure outcome so a green sign-off cannot hide a failed rerun.
 */
export function buildEffectiveRunOutcomeInput(
  run: RunOutcomeInput,
  effectiveSteps: ReadonlyArray<EffectiveRunStep>,
  effectiveRehost: {
    rehostStatus?: RunOutcomeInput['rehostStatus'];
    rehostCancelRequested?: boolean;
    message?: string;
    rehostCounts?: RehostCounts;
    rehostErrorSummary?: RunOutcomeInput['rehostErrorSummary'];
    heartbeatAt?: Date | string;
    rehostStartedAt?: Date | string;
    updatedAt?: Date | string;
  },
): RunOutcomeInput {
  const summary = summarizeEffectiveDepartmentMigration(effectiveSteps);
  const status = summary.status === 'running'
    ? 'running'
    : effectiveSteps.some((step) => step.status === 'failed')
      ? 'failed'
      : summary.status === 'completed' ? 'completed' : run.status;
  return {
    ...run,
    status,
    counts: {
      ...summary.counts,
      unchanged: effectiveSteps.reduce((sum, step) => sum + (step.counts.unchanged ?? 0), 0),
    },
    rehostStatus: effectiveRehost.rehostStatus,
    rehostCancelRequested: effectiveRehost.rehostCancelRequested ?? run.rehostCancelRequested,
    message: effectiveRehost.message ?? run.message,
    rehostCounts: effectiveRehost.rehostCounts,
    rehostErrorSummary: effectiveRehost.rehostErrorSummary,
    heartbeatAt: effectiveRehost.heartbeatAt ?? run.heartbeatAt,
    rehostStartedAt: effectiveRehost.rehostStartedAt ?? run.rehostStartedAt,
    updatedAt: effectiveRehost.updatedAt ?? run.updatedAt,
    skippedReasonSummary: undefined,
    steps: [...effectiveSteps],
  };
}

export function formatRehostProgressLine(input: {
  rehostStatus?: 'not_started' | 'running' | 'completed' | 'partial_failed';
  rehostCounts?: RehostCounts;
  hasDocumentRows?: boolean;
}): string {
  const counts = input.rehostCounts;
  if (counts && counts.total > 0) {
    const failed = counts.failed > 0 ? ` · ${counts.failed} failed` : '';
    const pending = counts.pending > 0 ? ` · ${counts.pending} pending` : '';
    return `files ${counts.copied}/${counts.total} copied${failed}${pending}`;
  }
  if (input.rehostStatus === 'partial_failed') return 'document re-host partial';
  if (input.hasDocumentRows === false) {
    return 'no legacy files to copy';
  }
  if (input.rehostStatus === 'not_started' || input.rehostStatus === 'running') {
    return 'document files not copied (metadata linked)';
  }
  return '';
}

export type FileCopyBadgeKind = 'none' | 'copying' | 'copy_stopped' | 'not_copied' | 'copied' | 'copy_failed';

/**
 * @reusable
 * Usage: Run History badge for Documents / department-wide apply.
 * Rules: metadata completed is not enough; copy outcome lives on rehostStatus.
 */
export function resolveFileCopyBadge(run: {
  datasetKey?: string;
  isGlobal?: boolean;
  status: string;
  rehostStatus?: 'not_started' | 'running' | 'completed' | 'partial_failed';
  rehostCounts?: RehostCounts;
  heartbeatAt?: Date | string;
  rehostStartedAt?: Date | string;
  updatedAt?: Date | string;
}, nowMs: number = Date.now()): FileCopyBadgeKind {
  const isDocuments = run.datasetKey === 'fileMetadata' || run.datasetKey === 'department-wide' || Boolean(run.isGlobal);
  if (!isDocuments || run.status === 'pending' || run.status === 'running' || run.status === 'cancelled') {
    return 'none';
  }
  if (run.rehostStatus === 'completed') return 'copied';
  if (run.rehostStatus === 'partial_failed') return 'copy_failed';
  if (run.rehostStatus === 'running') return isRehostCopyStale(run, nowMs) ? 'copy_stopped' : 'copying';
  if (run.status === 'completed') return 'not_copied';
  return 'none';
}

export type DepartmentMigrationCompletenessStatus = 'pending' | 'partial' | 'migrated' | 'in_progress' | 'stalled' | 'paused';

/**
 * @reusable
 * Usage: Left-list badge. Migrated when every occupancy DAG adapter is covered by a
 *        receipt or a completed department-wide / tab apply (empty datasets write no receipts).
 *        Cancelled leftover `skipped` steps are not coverage.
 */
export function departmentMigrationCompleteness(
  receiptDatasetKeys: readonly string[],
  requiredKeys: readonly string[] = DEPARTMENT_WIDE_ADAPTER_KEYS,
  coveredWithoutReceiptKeys: readonly string[] = [],
): { status: DepartmentMigrationCompletenessStatus; coveredKeys: string[]; missingKeys: string[]; receiptCount: number } {
  const covered = new Set([
    ...receiptDatasetKeys.filter(Boolean),
    ...coveredWithoutReceiptKeys.filter(Boolean),
  ]);
  const coveredKeys = requiredKeys.filter((key) => covered.has(key));
  const missingKeys = requiredKeys.filter((key) => !covered.has(key));
  const receiptCount = requiredKeys.filter((key) => receiptDatasetKeys.includes(key)).length;
  let status: DepartmentMigrationCompletenessStatus = 'pending';
  if (coveredKeys.length > 0 && missingKeys.length === 0) status = 'migrated';
  else if (coveredKeys.length > 0) status = 'partial';
  return { status, coveredKeys, missingKeys, receiptCount };
}

/**
 * @reusable
 * Usage: Left-list Partial vs Migrated map from Dexie receipts + runs.
 * Rules: Latest department-wide step keys are the required set. Completed DW
 *        steps and later tab apply runs count as coverage. Skipped leftover
 *        steps after cancel do not.
 */
export function buildDepartmentMigrationCompletenessByTenant(
  receipts: ReadonlyArray<{ tenantId?: string; datasetKey?: string; _deleted?: boolean }>,
  runs: ReadonlyArray<{
    tenantId?: string;
    datasetKey?: string;
    mode?: string;
    status?: string;
    startedAt?: string;
    heartbeatAt?: string;
    updatedAt?: string;
    _deleted?: boolean;
    steps?: Array<{
      adapterKey?: string;
      status?: string;
      message?: string;
      heartbeatAt?: string;
      progressedAt?: string;
      startedAt?: string;
      processed?: number;
    }>;
  }>,
  nowMs: number = Date.now(),
): Record<string, DepartmentMigrationCompletenessStatus> {
  const keysByTenant = new Map<string, Set<string>>();
  for (const receipt of receipts) {
    if (receipt._deleted || !receipt.tenantId || !receipt.datasetKey) continue;
    let keys = keysByTenant.get(receipt.tenantId);
    if (!keys) {
      keys = new Set<string>();
      keysByTenant.set(receipt.tenantId, keys);
    }
    keys.add(receipt.datasetKey);
  }

  const coveredByRun = new Map<string, Set<string>>();
  const requiredByTenant = new Map<string, string[]>();
  const latestDwStarted = new Map<string, string>();
  const latestLiveRun = new Map<string, { startedAt: string; stale: boolean }>();
  for (const run of runs) {
    if (run._deleted || !run.tenantId) continue;
    if (run.datasetKey === 'department-wide' && isDepartmentWideRunLive(run.status)) {
      const startedAt = String(run.startedAt ?? '');
      const current = latestLiveRun.get(run.tenantId);
      if (!current || startedAt >= current.startedAt) {
        latestLiveRun.set(run.tenantId, { startedAt, stale: isDepartmentWideRunStale(run, nowMs) });
      }
    }
    let covered = coveredByRun.get(run.tenantId);
    if (!covered) {
      covered = new Set<string>();
      coveredByRun.set(run.tenantId, covered);
    }
    if (run.datasetKey === 'department-wide' && Array.isArray(run.steps)) {
      const startedAt = String(run.startedAt ?? '');
      if (startedAt >= (latestDwStarted.get(run.tenantId) ?? '')) {
        latestDwStarted.set(run.tenantId, startedAt);
        requiredByTenant.set(
          run.tenantId,
          run.steps.map((step) => step?.adapterKey).filter((key): key is string => Boolean(key)),
        );
      }
      for (const step of run.steps) {
        if (step?.adapterKey && step.status === 'completed') covered.add(step.adapterKey);
      }
      continue;
    }
    if (run.mode === 'apply' && run.status === 'completed' && run.datasetKey) {
      covered.add(run.datasetKey);
    }
  }

  const tenantIds = new Set([...keysByTenant.keys(), ...coveredByRun.keys(), ...latestLiveRun.keys()]);
  const map: Record<string, DepartmentMigrationCompletenessStatus> = {};
  for (const tenantId of tenantIds) {
    const latestLive = latestLiveRun.get(tenantId);
    if (latestLive) {
      const activity = departmentRunActivity(runs.find((run) => run.tenantId === tenantId && run.datasetKey === 'department-wide' && String(run.startedAt ?? '') === latestLive.startedAt), nowMs);
      map[tenantId] = activity === 'paused' || latestLive.stale
        ? 'paused'
        : activity === 'stalled'
          ? 'stalled'
          : 'in_progress';
      continue;
    }
    map[tenantId] = departmentMigrationCompleteness(
      [...(keysByTenant.get(tenantId) ?? [])],
      requiredByTenant.get(tenantId),
      [...(coveredByRun.get(tenantId) ?? [])],
    ).status;
  }
  return map;
}
